import { JSONSchema7 } from 'json-schema';
import { PIXEL_EVENTS } from '../pixel';
import { createBackAnswerStateSchema } from './schema';

export function createICStateSchema() {
  return {
    type: 'object',
    description: 'Объект исчерпывающе описывающий состояние конкретного прохождения опроса',
    properties: {
      startedAt: { type: 'number' },
      finishedAt: { type: 'number' },
      questionId: { type: ['string', 'null'] },
      groupId: { type: ['string', 'null'] },
      welcomeId: { type: ['string', 'null'] },
      finalId: { type: ['string', 'null'] },
      pollsIc: { type: 'string' },
      readOnly: { type: 'boolean' },
      lastPixelHash: {
        type: ['string', 'null'],
        description:
          'Хэш данных последнего отправленного пикселя. Используется для проверки целостности данных на сервере',
      },
      links: {
        type: 'object',
        patternProperties: { '.*': { type: 'array', items: { type: 'string' } } },
        description: 'Граф связей между медиафайлами с типом "ссылка"',
      },
      externalLogicRules: {
        type: 'object',
        patternProperties: { '.*': { type: 'boolean' } },
        description: 'Ключ - название внешнего условия, значение - истинность условия',
      },
      questionsIndices: {
        type: 'array',
        items: { type: 'string' },
        description:
          'Индекс - позиция вопроса в опросе, значение - id вопроса. Необходимо для фиксации рандомизации при восстановлении состояние',
      },
      answersStack: {
        type: 'array',
        items: { $ref: '#/definitions/answerState' },
      },
      trackingStack: {
        type: 'array',
        items: { $ref: '#/definitions/trackingState' },
      },
      backAnswerStack: {
        type: 'array',
        items: { $ref: '#/definitions/backAnswerState' },
      },
    },
    additionalProperties: false,
    required: [
      'questionId',
      'groupId',
      'welcomeId',
      'finalId',
      'pollsIc',
      'answersStack',
      'trackingStack',
      'backAnswerStack',
    ],
    definitions: {
      answerState: createAnswerStateSchema(),
      trackingState: createTrackingStateSchema(),
      backAnswerState: createBackAnswerStateSchema(),
    },
  } as const;
}

export function createAnswerStateSchema(): JSONSchema7 {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      answersIds: { type: 'array', items: { type: 'string' } },
      answersOrder: { type: 'array', items: { type: 'number' } },
      answeredIds: { type: 'array', items: { type: 'string' } },
      answeredOthers: { type: 'string' },
      errorMessage: { type: 'string' },
      awardAmount: { type: 'number', description: 'Вознаграждение, целое число' },
      elapsedTime: { type: ['number', 'null'] },
      createdAt: { type: 'number' },
      stateId: { type: 'string' },
      isWithoutAnswers: { type: 'boolean' },
      isNotApplicable: { type: 'boolean' },
      isAutoCommit: { type: 'boolean' },
      isSkipped: {
        description: 'Если true, то вопрос был пропущен (при ответе на вопрос не был выбран ни один ответ)',
        type: 'boolean',
      },
      isAnswered: { description: 'Если true, то пользователь дал ответ и он был принят на отправку', type: 'boolean' },
      isCorrect: { type: 'boolean' },
      isTimeExpired: { enum: ['changedSinceExpired', 'unchangedSinceExpired'] },
      isStored: { description: 'Если true, то вся статистика по вопросу была успешно отправлена', type: 'boolean' },
      trackingId: { type: ['string'] },
      mediaProgress: { type: 'number' },
      lastTouchedBy: {
        description: `Указывает, кто последний раз взаимодействовал с вопросом.\nЗначение user означает, что изменения инициировал пользователь.\nSystem - означает, что изменения вызваны каким-то внешним событием, например применением прединициализированного состояния`,
        type: ['string', 'null'],
        enum: ['user', 'system'],
      },
    },
    additionalProperties: false,
    required: [
      'id',
      'stateId',
      'trackingId',
      'isWithoutAnswers',
      'isNotApplicable',
      'isAutoCommit',
      'isSkipped',
      'isAnswered',
      'isCorrect',
      'isStored',
      'lastTouchedBy',
    ],
  } as const;
}

export function createTrackingStateSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      type: { enum: Object.values(PIXEL_EVENTS) },
      tracking: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            isSystem: { type: 'boolean' },
            status: { enum: ['pending', 'rejected', 'fulfilled'] },
          },
          additionalProperties: false,
          required: ['id', 'isSystem', 'isSent'],
        } as const,
      },
    } as const,
    additionalProperties: false,
    required: ['id', 'type', 'tracking'],
  };
}
